/**
 * Post-Certificate Prompt Alpine.js Component
 *
 * Listens for the 'show-post-certificate-prompt' event (dispatched after
 * a certificate is successfully created) and shows a dialog offering to:
 * 1. Mark the linked compliance job as completed
 * 2. Send the certificate to current tenants via email
 */

export function postCertificatePrompt() {
  return {
    open: false,
    processing: false,
    actions: null,
    selectedJobIds: [],
    sendToTenants: true,
    sendToLandlords: true,
    chargeLandlord: false,
    chargeAmount: '',
    chargeVatRate: '20',
    chargeDescription: '',
    resultMessage: null,
    resultError: null,

    init() {
      this._handler = (event) => {
        this.actions = event.detail
        const openJobs = this.actions.open_jobs || []
        // Default: tick the first (best-match renewal) job only — agent can tick more if needed.
        this.selectedJobIds = openJobs.length > 0 ? [openJobs[0].id] : []
        this.sendToTenants = !!this.actions.can_send_to_tenants
        this.sendToLandlords = !!this.actions.can_send_to_landlords
        // The landlord agreed a quoted price when they answered the renewal
        // reminder — pre-tick and pre-fill the charge with it.
        this.chargeLandlord = !!this.actions.charge_default_net
        this.chargeAmount = this.actions.charge_default_net ? Number(this.actions.charge_default_net).toFixed(2) : ''
        this.chargeVatRate = '20'
        this.chargeDescription = this.actions.job_title || ''
        this.resultMessage = null
        this.resultError = null
        this.processing = false
        this.open = true
      }
      window.addEventListener('show-post-certificate-prompt', this._handler)
    },

    get completeJob() {
      return this.selectedJobIds.length > 0
    },

    destroy() {
      window.removeEventListener('show-post-certificate-prompt', this._handler)
    },

    close() {
      this.open = false
      this.actions = null
    },

    async confirm() {
      if (this.processing) return
      this.processing = true
      this.resultMessage = null
      this.resultError = null

      const csrfToken = document.querySelector('[name="csrf-token"]')?.content
      const results = []
      let hasError = false

      // Apply charge to landlord if selected (do this BEFORE completing the job)
      if (this.chargeLandlord && this.actions.can_charge_landlord && parseFloat(this.chargeAmount) > 0) {
        try {
          const response = await fetch(this.buildUrl('charge_landlord'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'X-CSRF-Token': csrfToken
            },
            body: JSON.stringify({
              job_id: this.actions.job_id,
              net_amount: this.chargeAmount,
              vat_rate: this.chargeVatRate,
              description: this.chargeDescription || this.actions.job_title || 'Certificate work'
            })
          })
          const data = await response.json()
          if (data.success) {
            results.push(data.message || `Landlord charged £${parseFloat(this.chargeAmount).toFixed(2)}`)
          } else {
            results.push(`Charge: ${data.error || 'Failed'}`)
            hasError = true
          }
        } catch (e) {
          results.push('Charge: Network error')
          hasError = true
        }
      }

      // Complete each selected job
      for (const jobId of this.selectedJobIds) {
        try {
          const response = await fetch(this.buildUrl('complete_job'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'X-CSRF-Token': csrfToken
            },
            body: JSON.stringify({
              job_id: jobId,
              certificate_id: this.actions.certificate_id
            })
          })
          const data = await response.json()
          if (data.success) {
            const jobInfo = (this.actions.open_jobs || []).find(j => j.id === jobId)
            results.push(`Job marked as completed: ${jobInfo?.title || `#${jobId}`}`)
          } else {
            results.push(`Job ${jobId}: ${data.error || 'Failed'}`)
            hasError = true
          }
        } catch (e) {
          results.push(`Job ${jobId}: Network error`)
          hasError = true
        }
      }

      // Send to tenants if selected
      if (this.sendToTenants && this.actions.can_send_to_tenants) {
        try {
          const response = await fetch(this.buildUrl('send_to_tenants'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'X-CSRF-Token': csrfToken
            },
            body: JSON.stringify({
              certificate_id: this.actions.certificate_id
            })
          })
          const data = await response.json()
          if (data.success) {
            results.push(data.message || 'Certificate queued for delivery')
          } else {
            results.push(`Email: ${data.error || 'Failed'}`)
            hasError = true
          }
        } catch (e) {
          results.push('Email: Network error')
          hasError = true
        }
      }

      // Send to landlords if selected
      if (this.sendToLandlords && this.actions.can_send_to_landlords) {
        try {
          const response = await fetch(this.buildUrl('send_to_landlords'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'X-CSRF-Token': csrfToken
            },
            body: JSON.stringify({
              certificate_id: this.actions.certificate_id
            })
          })
          const data = await response.json()
          if (data.success) {
            results.push(data.message || 'Certificate sent to landlord')
          } else {
            results.push(`Landlord email: ${data.error || 'Failed'}`)
            hasError = true
          }
        } catch (e) {
          results.push('Landlord email: Network error')
          hasError = true
        }
      }

      this.processing = false

      if (hasError) {
        this.resultError = results.join('. ')
      } else {
        this.close()

        // Close any open compliance slideover — the workflow is complete
        const slug = this.getCertificateTypeSlug()
        const eventMap = { gas_safety: 'gas-compliance', electrical: 'eicr-compliance' }
        const prefix = eventMap[slug] || 'gas-compliance'
        window.dispatchEvent(new CustomEvent(`close-${prefix}-slideover`))
      }
    },

    // Build the compliance workflow URL using the property_id and certificate_type_slug
    // from the page context.  The certificate index page embeds the slug; on the
    // property show page we derive it from the actions payload.
    buildUrl(action) {
      // The prompt always carries property_id in the broadcast data.
      // We need to figure out the certificate_type_slug. The actions hash
      // contains certificate_type_name, but we need the slug.  The simplest
      // approach: read it from the page's data attribute (set on the index),
      // or fall back to matching from the certificate data.
      const slug = this.getCertificateTypeSlug()
      const propertyId = this.getPropertyId()
      return `/properties/${propertyId}/compliance/${slug}/${action}`
    },

    getCertificateTypeSlug() {
      // On the certificate index page, the slug is in the URL
      const match = window.location.pathname.match(/\/certificates\/([^/]+)/)
      if (match) return match[1]

      // Fall back: derive from the certificate type name
      const name = this.actions?.certificate_type_name || ''
      if (name.toLowerCase().includes('gas')) return 'gas_safety'
      if (name.toLowerCase().includes('eicr') || name.toLowerCase().includes('electrical')) return 'electrical'
      if (name.toLowerCase().includes('epc')) return 'epc'
      return 'gas_safety'
    },

    getPropertyId() {
      // First check if it was passed in the actions detail (set by certificate modal)
      if (this.actions?.property_id) return this.actions.property_id

      // On the property show page, the property ID is in the URL
      const showMatch = window.location.pathname.match(/\/properties\/(\d+)/)
      if (showMatch) return showMatch[1]

      return ''
    },

    get hasActions() {
      return this.actions && (this.actions.can_complete_job || this.actions.can_send_to_tenants || this.actions.can_send_to_landlords || this.actions.can_charge_landlord)
    },

  }
}
